import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { FileUpload } from "@/components/admin/FileUpload";

const POSITIONS = [
  { value: "after_hero", label: "After Hero" },
  { value: "after_why", label: "After Why Choose Us" },
  { value: "after_services", label: "After Services" },
  { value: "after_facilities", label: "After Facilities" },
  { value: "after_departments", label: "After Departments" },
  { value: "after_doctors", label: "After Doctors" },
  { value: "after_testimonials", label: "After Testimonials" },
  { value: "after_cta", label: "After Emergency CTA" },
];

const LAYOUTS = [
  { value: "single", label: "Single (full-width)" },
  { value: "two_col", label: "Two Columns" },
  { value: "three_col", label: "Three Columns" },
  { value: "four_col", label: "Four Columns" },
  { value: "slider", label: "Slider / Carousel" },
  { value: "gallery", label: "Image Gallery" },
  { value: "video", label: "Video (single)" },
  { value: "banner", label: "Banner with overlay" },
];

const BACKGROUNDS = [
  { value: "white", label: "White" },
  { value: "muted", label: "Muted (light grey)" },
  { value: "accent_red", label: "Red accent" },
  { value: "dark", label: "Dark" },
];

const EMPTY = { overline: "", title: "", subtitle: "", position: "after_doctors", layout: "three_col", background: "white", enabled: true, order: 0, items: [] };

export default function AdminHomeSections() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);          // section being edited, or null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/home-sections"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditing(s); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = () => setF("items", [...form.items, { id: `tmp-${Date.now()}`, image_url: "", video_url: "", title: "", caption: "", cta_label: "", cta_url: "", order: form.items.length }]);
  const updItem = (idx, patch) => setF("items", form.items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const delItem = (idx) => setF("items", form.items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const arr = [...form.items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setF("items", arr.map((it, i) => ({ ...it, order: i })));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, items: form.items.map((it, i) => ({ ...it, order: i })) };
      if (editing?.id) await api.put(`/admin/home-sections/${editing.id}`, payload);
      else await api.post("/admin/home-sections", payload);
      toast.success(editing?.id ? "Updated" : "Created");
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete section "${s.title || 'Untitled'}"?`)) return;
    try { await api.delete(`/admin/home-sections/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (s) => {
    try {
      const payload = { ...s, enabled: !s.enabled };
      await api.put(`/admin/home-sections/${s.id}`, payload);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-home-sections-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900">Home Sections</h1>
          <p className="text-slate-500 mt-1">Dynamic banners, sliders, galleries and videos — placed anywhere on the home page.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="section-new"><Plus className="w-4 h-4" /> New Section</button>
      </div>

      <div className="mt-8 card-medi overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Title</th>
                <th className="text-left px-6 py-3 font-medium">Position</th>
                <th className="text-left px-6 py-3 font-medium">Layout</th>
                <th className="text-left px-6 py-3 font-medium">Items</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="px-6 py-6 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-6 py-8 text-center text-slate-500">No sections yet — click <b>New Section</b> to add one.</td></tr>}
              {items.map((s) => (
                <tr key={s.id} data-testid={`section-row-${s.id}`}>
                  <td className="px-6 py-4 font-medium text-slate-800">{s.title || <span className="italic text-slate-400">Untitled</span>}</td>
                  <td className="px-6 py-4 text-slate-600">{POSITIONS.find(p => p.value === s.position)?.label || s.position}</td>
                  <td className="px-6 py-4 text-slate-600">{LAYOUTS.find(l => l.value === s.layout)?.label || s.layout}</td>
                  <td className="px-6 py-4 text-slate-600">{s.items?.length || 0}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => toggle(s)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${s.enabled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`section-toggle-${s.id}`}>
                      {s.enabled ? <><Eye className="w-3 h-3" /> Live</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`section-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(s)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`section-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4 overflow-y-auto" data-testid="section-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <h3 className="font-heading font-semibold text-slate-900">{editing?.id ? "Edit" : "New"} Home Section</h3>
              <button onClick={close} className="p-2 rounded-lg hover:bg-slate-100" data-testid="section-close"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Overline (small text above title)"><input type="text" value={form.overline} onChange={(e) => setF("overline", e.target.value)} className={inputCls} data-testid="section-overline" /></Field>
                <Field label="Title"><input type="text" value={form.title} onChange={(e) => setF("title", e.target.value)} className={inputCls} data-testid="section-title" /></Field>
              </div>
              <Field label="Subtitle"><textarea rows={2} value={form.subtitle} onChange={(e) => setF("subtitle", e.target.value)} className={inputCls} data-testid="section-subtitle" /></Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Position">
                  <select value={form.position} onChange={(e) => setF("position", e.target.value)} className={inputCls} data-testid="section-position">
                    {POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </Field>
                <Field label="Layout">
                  <select value={form.layout} onChange={(e) => setF("layout", e.target.value)} className={inputCls} data-testid="section-layout">
                    {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </Field>
                <Field label="Background">
                  <select value={form.background} onChange={(e) => setF("background", e.target.value)} className={inputCls} data-testid="section-bg">
                    {BACKGROUNDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Order (lower shows first)"><input type="number" value={form.order} onChange={(e) => setF("order", e.target.value)} className={inputCls} data-testid="section-order" /></Field>
                <label className="flex items-end gap-2 text-sm text-slate-700 pb-2 cursor-pointer">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setF("enabled", e.target.checked)} className="rounded border-slate-300 text-red-600 focus:ring-red-500" data-testid="section-enabled" />
                  Show this section on the home page
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-heading font-medium text-slate-900">Items</div>
                    <div className="text-xs text-slate-500">Each item becomes a card / slide / gallery image / banner content.</div>
                  </div>
                  <button type="button" onClick={addItem} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-red-50 text-red-700 hover:bg-red-100" data-testid="section-add-item"><Plus className="w-4 h-4" /> Add item</button>
                </div>
                {form.items.length === 0 && <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-xl">No items yet.</div>}
                <div className="space-y-4">
                  {form.items.map((it, idx) => (
                    <div key={it.id} className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Item #{idx + 1}</div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveItem(idx, -1)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button type="button" onClick={() => moveItem(idx, 1)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                          <button type="button" onClick={() => delItem(idx)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Title"><input type="text" value={it.title} onChange={(e) => updItem(idx, { title: e.target.value })} className={inputCls} data-testid={`item-title-${idx}`} /></Field>
                        <Field label="CTA label (button text)"><input type="text" value={it.cta_label} onChange={(e) => updItem(idx, { cta_label: e.target.value })} className={inputCls} data-testid={`item-cta-label-${idx}`} /></Field>
                      </div>
                      <Field label="Caption / description"><textarea rows={2} value={it.caption} onChange={(e) => updItem(idx, { caption: e.target.value })} className={inputCls} data-testid={`item-caption-${idx}`} /></Field>
                      <Field label="CTA link (URL)"><input type="url" value={it.cta_url} onChange={(e) => updItem(idx, { cta_url: e.target.value })} className={inputCls} data-testid={`item-cta-url-${idx}`} /></Field>
                      <Field label="Image">
                        <FileUpload value={it.image_url} onChange={(v) => updItem(idx, { image_url: v })} accept="image/*" label="Upload image" testid={`item-image-${idx}`} />
                      </Field>
                      <Field label="Video (YouTube/Vimeo URL, or MP4 upload)">
                        <FileUpload value={it.video_url} onChange={(v) => updItem(idx, { video_url: v })} accept="video/*" label="Upload video" testid={`item-video-${idx}`} />
                      </Field>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 sticky bottom-0 bg-white -mx-6 px-6 pb-2">
                <button type="button" onClick={close} className="px-5 py-2.5 rounded-full text-slate-600 hover:bg-slate-100">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="section-save">{busy ? "Saving…" : "Save section"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}
